export type DateRange = [Date | null, Date | null];

export type Filters = {
  productType?: string | null;
  parameters: string[];
  domains: string[];
  countries: string[];
};

export type FilterOptions = {
  domainCount: number;
  domains: string[];
  countries: string[];
  productTypes: string[];
  parameters: string[];
};

// Graph Data from API (its interface)
export interface AnalyticsGraphDataPoint {
  day: string;
  successful_requests: number | string;
  failed_requests: number | string;
  cancelled_requests: number | string;
  avg_concurrency?: number | string | null;
}

export interface AnalyticsDomainResponseItem {
  domain: string;
  product_type?: string | null;
  parameters?: string[];
  location?: string | null;
  requests: number | string;
  successful_requests?: number | string;
  failed_requests?: number | string;
  cancelled_requests?: number | string;
  avg_concurrency?: number | string | null;
  credit_costs?: number | string;
  ultra_premium_count?: number | string;
  premium_count?: number | string;
  render_count?: number | string;
  aiparser_credits?: number | string;
  success_rate: number | string;
}

export interface FilterOptionsResponse {
  totalDomainCount?: number | string;
  domains?: string[];
  countries?: string[];
  productTypes?: string[];
  parameters?: string[];
  invalidSelectedDomains?: string[];
  invalidSelectedCountries?: string[];
}

// format for the charts on UI
export interface ChartsData {
  startOfInterval: string;
  success?: number;
  failure: number;
  concurrentThreads: number;
}

export type OverviewStats = {
  total_requests: number;
  successful_requests: number;
  avg_response_time_ms: number;
  avg_concurrency: number;
  total_credits: number;
  domains_scraped: number;
  total_payg_requests: number;
  total_payg_credits: number;
  cancelled_requests: number;
  cancelled_credits: number;
  success_rate: number; // Calculated by backend
  credits_per_request: number; // Calculated by backend
};

export type AiparserCreationTotals = {
  parsers_created: number;
  successful: number;
  failed: number;
  promotional: number;
  credit_cost: number;
};

export type AiparserCreationsByDomain = {
  domain: string;
  parsers_created: number;
  successful: number;
  failed: number;
  promotional: number;
  credit_cost: number;
};

export type AiparserCreationsByDay = {
  day: string;
  parsers_created: number;
  successful: number;
  failed: number;
  credit_cost: number;
};

export type AiparserCreations = {
  totals: AiparserCreationTotals;
  by_domain: AiparserCreationsByDomain[];
  by_day: AiparserCreationsByDay[];
};

// UI-friendly summary for quick stats cards
export type OverviewStatsSummary = {
  requestsSent: number;
  successRate: number;
  avgResponseTime: number;
  avgConcurrency: number;
  domainsScraped: number;
  creditsPerRequest: number;
  paygRequests: number;
  paygCredits: number;
  cancelledRequests: number;
  cancelledCredits: number;
  parsersCreated: number;
  parserCreditsConsumed: number;
};

export type DomainData = {
  list: AnalyticsDomainResponseItem[];
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
};

export type DomainDetailsRow = {
  day: string;
  successful_requests: number | string;
  failed_requests: number | string;
  cancelled_requests?: number | string;
  avg_response_time_ms?: number | string | null;
  avg_concurrency?: number | string | null;
  response_time_sum_ms?: number | string | null;
  response_time_count?: number | string;
  concurrency_sum?: number | string | null;
  concurrency_count?: number | string;
  products?: string[];
  structured_endpoints?: string[];
  countries?: string[];
  total_for_day?: number | string;
};

export type DomainSearchResult = {
  domain: string;
  count: number;
};

export type SeverityLevel = "low" | "medium" | "high";

export type ErrorLogsFilters = {
  searchTerm: string;
  severity: SeverityLevel | null;
  statusCodes: string[];
  domains: string[];
};

// Valid severity levels - used for type safety and UI rendering
export const SEVERITY_LEVELS: readonly SeverityLevel[] = ["low", "medium", "high"];

// Raw error log entry from API
export type ErrorLogEntry = {
  request_id: string;
  timestamp: string;
  domain: string;
  url: string;
  status_code: number;
  termination_reason: string;
  retries: number;
  request_text: string;
  severity: SeverityLevel; // Calculated by backend
  status_message: string; // User-friendly message from backend
};

// Enriched error log with UI state (selection, visibility)
export type EnrichedErrorLogEntry = ErrorLogEntry & {
  isSelected: boolean;
  isHidden: boolean;
};

// Error log table row: overview rows have no UI flags, full-page rows do
export type ErrorLogRow = ErrorLogEntry & { isSelected?: boolean; isHidden?: boolean };

export type ErrorLogsData = {
  list: EnrichedErrorLogEntry[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filterOptions: {
    domains: string[];
    statusCodes: string[];
  };
};

export type AnalyticsOverviewResponse = {
  stats: OverviewStats;
  graph: AnalyticsGraphDataPoint[];
  domains: AnalyticsDomainResponseItem[];
  errors: ErrorLogEntry[];
  availableFilters: FilterOptionsResponse;
  aiparserCreations: AiparserCreations | null;
};

export type ErrorLogsResponse = {
  errors: ErrorLogEntry[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  filterOptions: {
    domains: string[];
    totalDomains: number;
    statusCodes: number[];
    severities: SeverityLevel[];
  };
};

export type SelectedErrorColumns = {
  [tableType: string]: string[];
};

// Domain table row data for UI display
export type DomainTableRow = {
  domain: string;
  requests: string; // Formatted with locale
  cancelledRequests: string; // Formatted with locale
  successRate: string; // Formatted as percentage
  credits: string; // Formatted with locale
  ultraPremium: string; // Formatted with locale
  premium: string; // Formatted with locale
  render: string; // Formatted with locale
  aiParser: string; // Formatted with locale
  location?: string;
};

// Stats shown when a domain row is expanded (fetched per domain)
export type DomainStats = {
  cumulativeData: {
    date: string;
    cumulative_success: number;
    cumulative_failure: number;
    concurrent_threads: number;
  }[];
  avgConcurrency: number;
  avgResponseTime: number;
  totalSuccess: number;
  totalFailure: number;
  products: string[];
  countries: string[];
};
